// ===========================================
// COMPONENTE: Lista de Usuarios (Multi-Tenant)
// ===========================================

import { useState } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  MoreHorizontal,
  Trash2,
  UserCheck,
  UserX,
  Shield,
  Loader2,
  KeyRound,
  Lock,
  Unlock,
  SlidersHorizontal,
} from 'lucide-react'
import { useAuth0 } from '@auth0/auth0-react'
import type { RolMultiTenant } from '@/types/roles'
import { ROL_MULTITENANT_CONFIG } from '@/types/roles'
import type { UsuarioConMembresia } from '@/services/usuariosService'

interface UsuariosListProps {
  usuarios: UsuarioConMembresia[]
  isLoading?: boolean
  onEliminar: (usuario: UsuarioConMembresia) => void
  onToggleBloqueo: (usuario: UsuarioConMembresia, bloquear: boolean) => void
  onCambiarRol: (usuario: UsuarioConMembresia, nuevoRol: RolMultiTenant) => void
  onResetPassword: (usuario: UsuarioConMembresia) => void
  onEditarPermisos: (usuario: UsuarioConMembresia) => void
}

export function UsuariosList({
  usuarios,
  isLoading,
  onEliminar,
  onToggleBloqueo,
  onCambiarRol,
  onResetPassword,
  onEditarPermisos,
}: UsuariosListProps) {
  const { user: currentUser } = useAuth0()
  const [usuarioEliminar, setUsuarioEliminar] = useState<UsuarioConMembresia | null>(null)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Cargando usuarios...</span>
      </div>
    )
  }

  if (usuarios.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No hay usuarios registrados en esta organización
      </div>
    )
  }

  const formatearFecha = (fecha: string | null) => {
    if (!fecha) return '-'
    return new Date(fecha).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  const esUsuarioActual = (email: string) => currentUser?.email === email

  const rolesDisponibles: RolMultiTenant[] = ['owner', 'admin', 'profesional', 'asistente', 'solo_lectura']

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Usuario</TableHead>
              <TableHead>Rol</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Workspaces</TableHead>
              <TableHead>Fecha Alta</TableHead>
              <TableHead className="w-[70px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {usuarios.map((usuario) => {
              const rolConfig = usuario.rol ? ROL_MULTITENANT_CONFIG[usuario.rol] : null
              const esMiUsuario = esUsuarioActual(usuario.email)

              return (
                <TableRow key={usuario.id}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">
                        {usuario.nombre || 'Sin nombre'}
                        {esMiUsuario && (
                          <span className="ml-2 text-xs text-muted-foreground">(Tú)</span>
                        )}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {usuario.email}
                      </span>
                      {!usuario.auth0_user_id && (
                        <span className="text-xs text-amber-600">Sin Auth0</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {rolConfig ? (
                      <Badge variant="outline" className={rolConfig.bgClass}>
                        <Shield className="h-3 w-3 mr-1" />
                        {rolConfig.label}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-gray-100 text-gray-800">
                        Sin rol
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {usuario.activo ? (
                      <Badge variant="outline" className="bg-green-100 text-green-800">
                        <UserCheck className="h-3 w-3 mr-1" />
                        Activo
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-red-100 text-red-800">
                        <UserX className="h-3 w-3 mr-1" />
                        Bloqueado
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {usuario.workspaces && usuario.workspaces.length > 0 ? (
                        usuario.workspaces.map((ws) => (
                          <Badge key={ws} variant="secondary" className="text-xs">
                            {ws}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatearFecha(usuario.created_at)}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {/* Reset Password */}
                        {usuario.auth0_user_id && (
                          <DropdownMenuItem onClick={() => onResetPassword(usuario)}>
                            <KeyRound className="h-4 w-4 mr-2" />
                            Enviar reset de contraseña
                          </DropdownMenuItem>
                        )}

                        <DropdownMenuSeparator />

                        {/* Cambiar rol */}
                        {!esMiUsuario && usuario.rol && (
                          <>
                            {rolesDisponibles
                              .filter((r) => r !== usuario.rol)
                              .map((rol) => (
                                <DropdownMenuItem
                                  key={rol}
                                  onClick={() => onCambiarRol(usuario, rol)}
                                >
                                  <Shield className="h-4 w-4 mr-2" />
                                  Cambiar a {ROL_MULTITENANT_CONFIG[rol].label}
                                </DropdownMenuItem>
                              ))}

                            <DropdownMenuSeparator />
                          </>
                        )}

                        {/* Editar permisos */}
                        {!esMiUsuario && usuario.rol && (
                          <>
                            <DropdownMenuItem onClick={() => onEditarPermisos(usuario)}>
                              <SlidersHorizontal className="h-4 w-4 mr-2" />
                              Permisos
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                          </>
                        )}

                        {/* Bloquear/Desbloquear */}
                        {!esMiUsuario && usuario.auth0_user_id && (
                          <DropdownMenuItem
                            onClick={() => onToggleBloqueo(usuario, usuario.activo)}
                          >
                            {usuario.activo ? (
                              <>
                                <Lock className="h-4 w-4 mr-2" />
                                Bloquear usuario
                              </>
                            ) : (
                              <>
                                <Unlock className="h-4 w-4 mr-2" />
                                Desbloquear usuario
                              </>
                            )}
                          </DropdownMenuItem>
                        )}

                        {/* Eliminar */}
                        {!esMiUsuario && usuario.auth0_user_id && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setUsuarioEliminar(usuario)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Eliminar
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      {/* Dialog de confirmación de eliminación */}
      <AlertDialog
        open={!!usuarioEliminar}
        onOpenChange={(open) => !open && setUsuarioEliminar(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar usuario</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de eliminar al usuario{' '}
              <strong>{usuarioEliminar?.nombre || usuarioEliminar?.email}</strong>?
              <br />
              <br />
              Esta acción eliminará el usuario de Auth0 y de la base de datos.
              No se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (usuarioEliminar) {
                  onEliminar(usuarioEliminar)
                  setUsuarioEliminar(null)
                }
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
